import { useState, useEffect } from "react";
import { Button, Modal, Message, Loader, List } from "semantic-ui-react";
import PropTypes from "prop-types";

import CampaignScheduleService from "services/CampaignScheduleService";

function ScheduleConfirmationModal({
  open,
  onClose,
  onConfirm,
  campaignId,
  deliveryTimeMode,
  deliveryTimeDisplay,
  scheduleOptionMode,
  runUntilDisplay,
  recipientsMode,
  filtersSummary,
  conditions,
  emailTemplateId,
  isSendingToAllContacts,
  sendToAllEmailAddresses,
}) {
  const [emailCount, setEmailCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEmailCount();
    } else {
      setEmailCount(null);
      setError(null);
      setConfirming(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchEmailCount = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = {
        conditions: conditions,
        email_template_id: emailTemplateId,
        sending_to_all_contacts: isSendingToAllContacts,
        info: {
          send_to_all_email_addresses: sendToAllEmailAddresses,
        },
      };
      const response =
        await CampaignScheduleService.getEmailableContactsForSchedule(
          campaignId,
          data
        );
      setEmailCount(response.extra?.email_count ?? 0);
    } catch (err) {
      console.error("Error fetching email count:", err);
      setError("Failed to fetch email count. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } catch (err) {
      console.error("Error confirming schedule:", err);
      setConfirming(false);
    }
  };

  const getEmailCount = () => {
    return loading ? (
      <Loader active inline size="mini" />
    ) : (
      <span
        style={{
          fontWeight: "bold",
          color: emailCount === 0 ? "#db2828" : "#21ba45",
        }}
      >
        {emailCount}
      </span>
    );
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>Confirm Scheduler Configuration</Modal.Header>
      <Modal.Content>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5em",
          }}
        >
          {error && <Message error content={error} />}
          <p style={{ fontWeight: "bold" }}>
            Please confirm all the details before proceeding
          </p>
          <div>
            <p style={{ fontWeight: "bold" }}>Delivery time</p>
            <p>Mode: {deliveryTimeMode}</p>
            <p>Scheduled for: {deliveryTimeDisplay || <em>Not set</em>}</p>
          </div>
          <div>
            <p style={{ fontWeight: "bold" }}>Schedule Option</p>
            <p>Mode: {scheduleOptionMode}</p>
            <p>Scheduled limit: {runUntilDisplay || <em>Not set</em>}</p>
          </div>
          <div>
            <p style={{ fontWeight: "bold" }}>Recipients</p>
            <p>
              Send to all enabled email addresses per Contact:{" "}
              {sendToAllEmailAddresses ? "Yes" : "No"}
            </p>
            <p>Mode: {recipientsMode}</p>
            {filtersSummary && !!filtersSummary.length && (
              <List bulleted>
                {filtersSummary.map((filter, index) => (
                  <List.Item key={`${index} ${filter}`}>{filter}</List.Item>
                ))}
              </List>
            )}
            <p>Number of emails to be sent: {getEmailCount()} emails</p>
          </div>
        </div>

        {emailCount === 0 && !loading && (
          <Message
            warning
            header="Based on the current filters, no emails will be sent. Please review your conditions."
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          secondary
          onClick={handleConfirm}
          disabled={loading || confirming}
          loading={confirming}
        >
          Continue
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

ScheduleConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  campaignId: PropTypes.number.isRequired,
  deliveryTimeMode: PropTypes.string,
  deliveryTimeDisplay: PropTypes.string,
  scheduleOptionMode: PropTypes.string,
  runUntilDisplay: PropTypes.string,
  recipientsMode: PropTypes.string,
  filtersSummary: PropTypes.arrayOf(PropTypes.string),
  conditions: PropTypes.array,
  emailTemplateId: PropTypes.number,
  isSendingToAllContacts: PropTypes.bool,
  sendToAllEmailAddresses: PropTypes.bool,
};

export default ScheduleConfirmationModal;
